import React, { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletProvider';
import { useToast, readableError } from '../components/Toast';
import Loader from '../components/Loader';
import { fetchUser, register, resolveAccount } from '../services/zen';
import { saveProfile, getProfile, getNames } from '../services/api';
import { shortAddr, fmtDate } from '../utils/format';
import { getRef, clearRef } from '../utils/referral';

export default function Account({ roles, onRegistered }) {
  const { provider, signer, address, isCorrectNetwork, disconnect, ensureNetwork } = useWallet();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [txBusy, setTxBusy] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  // sponsor MT5 captured at app load (referral link now carries an MT5 number)
  const capturedRef = getRef() || '';
  const [form, setForm] = useState({ name: '', mobile: '', email: '', account: '', sponsorMt5: capturedRef });

  // live sponsor resolution: { status: 'idle'|'checking'|'ok'|'bad', id, name }
  const [sponsor, setSponsor] = useState({ status: 'idle', id: '0', name: '' });

  const load = async () => {
    if (!provider || !address || !isCorrectNetwork) { setLoading(false); return; }
    try {
      setLoading(true);
      const u = await fetchUser(provider, address);
      setUser(u);
      if (u.registered) {
        const p = await getProfile(address);
        setProfile(p);
      }
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await load(); })();
    return () => { alive = false; };
  }, [provider, address, isCorrectNetwork]);

  // debounced resolve of sponsor MT5 -> on-chain id -> DB name
  useEffect(() => {
    const mt5 = (form.sponsorMt5 || '').replace(/[^0-9]/g, '');
    if (!mt5) { setSponsor({ status: 'idle', id: '0', name: '' }); return; }
    if (!provider || !isCorrectNetwork) return;

    let alive = true;
    setSponsor((s) => ({ ...s, status: 'checking' }));
    const t = setTimeout(async () => {
      try {
        const id = await resolveAccount(provider, mt5);
        if (!alive) return;
        if (id === '0') { setSponsor({ status: 'bad', id: '0', name: '' }); return; }
        let name = '';
        try { const nm = await getNames([id]); name = nm[id]?.name || ''; } catch (_) {}
        if (alive) setSponsor({ status: 'ok', id, name: name || '(name unavailable)' });
      } catch (_) {
        if (alive) setSponsor({ status: 'bad', id: '0', name: '' });
      }
    }, 450);

    return () => { alive = false; clearTimeout(t); };
  }, [form.sponsorMt5, provider, isCorrectNetwork]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onRegister = async () => {
    const account = form.account.replace(/[^0-9]/g, '');
    if (!account) { toast.error('Enter your MT5 account number'); return; }
    if (!form.name.trim()) { toast.error('Enter your name'); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error('Enter a valid email'); return; }
    if (form.mobile.replace(/[^0-9]/g, '').length < 7) { toast.error('Enter a valid mobile number'); return; }
    if (!(await ensureNetwork())) { toast.error('Please switch to the correct network'); return; }

    // resolve sponsor: blank -> company default; otherwise MT5 -> on-chain id
    const sponsorMt5 = (form.sponsorMt5 || '').replace(/[^0-9]/g, '');
    let referrer;
    if (!sponsorMt5) {
      referrer = roles ? roles.defaultReferrerId : '0';
    } else {
      const id = await resolveAccount(provider, sponsorMt5);
      if (id === '0') {
        toast.error('Sponsor MT5 account not found. Check the number or leave it blank to join under the company.');
        return;
      }
      referrer = id;
    }

    try {
      setTxBusy(true);
      // 1) on-chain registration (wallet + account + sponsor id)
      const receipt = await register(signer, referrer, account);

      // 2) read back the new user id, then save PII + trigger welcome email via PHP
      const u = await fetchUser(provider, address);
      try {
        await saveProfile({
          wallet: address,
          userId: u.id,
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          email: form.email.trim(),
          accountNumber: account,
          referrerId: referrer,
        });
      } catch (apiErr) {
        // chain succeeded; DB save failed — let them know but don't block
        toast.info('Registered on-chain. Profile sync will retry.');
      }

      toast.success('Registration complete! Welcome to Zen Capital.');
      clearRef();
      await load();
      if (onRegistered) onRegistered();
    } catch (e) {
      toast.error(readableError(e));
    } finally {
      setTxBusy(false);
    }
  };

  if (loading) return <Loader messages={['Loading account']} />;

  // ---------- registered: profile view ----------
  if (user && user.registered) {
    return (
      <div>
        <h1 className="page-title">Account</h1>
        <div className="card">
          <div className="card-title">Profile</div>
          <Row k="Name" v={(profile && profile.name) || '—'} />
          <Row k="MT5 Account" v={user.accountNumber} />
          <Row k="Email" v={(profile && profile.email) || '—'} />
          <Row k="Mobile" v={(profile && profile.mobile) || '—'} />
          <Row k="Member ID" v={user.id} accent="blue" />
          <Row k="Sponsor ID" v={user.referrerId} />
          <Row k="Wallet" v={shortAddr(user.wallet)} />
          <Row k="Joined" v={fmtDate(user.registrationTime)} />
        </div>
        <div className="spacer-18" />
        <button className="btn ghost" onClick={disconnect}>Disconnect Wallet</button>
      </div>
    );
  }

  // ---------- not registered: form ----------
  const sponsorOk = sponsor.status === 'ok';
  const sponsorBad = sponsor.status === 'bad';
  const hasSponsorInput = (form.sponsorMt5 || '').replace(/[^0-9]/g, '') !== '';

  return (
    <div>
      {txBusy && <Loader messages={['Confirm in your wallet', 'Registering on-chain', 'Sending welcome email']} />}
      <h1 className="page-title">Register</h1>

      {hasSponsorInput
        ? (sponsorOk
            ? <div className="banner">Sponsor: <b>{sponsor.name}</b> <span className="mono" style={{ color: 'var(--blue-bright)' }}>(MT5 {form.sponsorMt5.replace(/[^0-9]/g, '')})</span></div>
            : sponsorBad
              ? <div className="banner bad">No registered account found for that sponsor MT5 number.</div>
              : <div className="banner">Checking sponsor…</div>)
        : <div className="banner warn">No referral link detected — you'll join under the company default.</div>}

      <div className="card">
        <div className="field"><label>Your MT5 Account Number</label>
          <input inputMode="numeric" placeholder="e.g. 53504089" value={form.account} onChange={set('account')} /></div>
        <div className="field"><label>Full Name</label>
          <input placeholder="Your name" value={form.name} onChange={set('name')} /></div>
        <div className="field"><label>Email</label>
          <input type="email" placeholder="you@email.com" value={form.email} onChange={set('email')} /></div>
        <div className="field"><label>Mobile Number</label>
          <input inputMode="tel" placeholder="+91…" value={form.mobile} onChange={set('mobile')} /></div>
        <div className="field">
          <label>Sponsor MT5 Account</label>
          <input inputMode="numeric" placeholder="Sponsor's MT5 number" value={form.sponsorMt5} onChange={set('sponsorMt5')} />
          {hasSponsorInput && (
            <div style={{ marginTop: 7, fontSize: 12 }}>
              {sponsor.status === 'checking' && <span className="muted">Checking…</span>}
              {sponsorOk && <span style={{ color: 'var(--up)' }}>✓ {sponsor.name}</span>}
              {sponsorBad && <span style={{ color: 'var(--down)' }}>✗ Not found — check the number or leave blank</span>}
            </div>
          )}
        </div>

        <button className="btn" onClick={onRegister} disabled={txBusy || (hasSponsorInput && !sponsorOk)}>
          {txBusy ? <span className="btn-spin" /> : 'Create Account'}
        </button>
      </div>
      <div className="spacer-12" />
      <p className="muted center" style={{ fontSize: 11.5 }}>
        Your name, email and mobile are stored securely off-chain. Only your account number and wallet go on-chain.
      </p>
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="row-between" style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
      <span className="muted" style={{ fontSize: 12.5 }}>{k}</span>
      <span className={'mono' + (accent ? ' ' : '')} style={{ fontSize: 13, color: accent === 'blue' ? 'var(--blue-bright)' : 'var(--text)' }}>{v}</span>
    </div>
  );
}
